import { designLayers } from '../interaction';
import { Box, Divider, Paper, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { designTokens } from '../tokens';
import { cardSurfaceStyles } from './cardSurface';
import { HelpPopover } from './HelpPopover';

export type SectionCardProps = {
  title: ReactNode;
  help?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  /** compact는 16px, standard·spacious는 작은 화면 16px·넓은 화면 24px입니다. none은 사용자 정의 헤더가 여백을 소유합니다. */
  headerPadding?: 'none' | 'compact' | 'standard' | 'spacious';
  /** 본문이 접힌 경우 헤더 구분선을 숨깁니다. 기본값은 true입니다. */
  showDivider?: boolean;
  /** standard와 spacious는 작은 화면 16px·넓은 화면 24px로 제목과 본문을 정렬합니다. */
  bodyPadding?: 'none' | 'standard' | 'spacious';
  /** 긴 카드 편집에서 같은 카드의 제목과 작업을 스크롤 중에도 유지합니다. */
  stickyHeader?: boolean;
};

export function SectionCard({
  title,
  help,
  action,
  children,
  bodyPadding = 'none',
  headerPadding = bodyPadding === 'none' ? 'compact' : bodyPadding,
  showDivider = true,
  stickyHeader = false,
}: SectionCardProps) {
  const spaciousInset = {
    xs: `${designTokens.layout.surfacePaddingCompact}px`,
    sm: `${designTokens.layout.surfacePadding}px`,
  };
  const headerInset = headerPadding === 'none' ? 0 : headerPadding === 'compact'
    ? `${designTokens.layout.surfacePaddingCompact}px`
    : spaciousInset;
  return (
    <Paper variant="outlined" sx={{ ...cardSurfaceStyles, overflow: 'visible' }}>
      <Box data-slot="header" sx={stickyHeader ? {
        position: 'sticky', top: 0, zIndex: designLayers.stickySection,
        bgcolor: designTokens.color.canvas,
        borderTopLeftRadius: designTokens.radius.surface, borderTopRightRadius: designTokens.radius.surface,
      } : undefined}>
      <Stack
        direction="row"
        sx={{
          minHeight: 48,
          px: headerInset,
          py: headerPadding === 'none' ? 0 : `${designTokens.layout.surfacePaddingCompact}px`,
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box sx={{ flex: '1 1 auto', minWidth: 0, display: 'flex', alignItems: 'center', gap: `${designTokens.space.xxs}px` }}>
          <Typography component="div" variant="h6" sx={{ flex: help === undefined ? '1 1 auto' : undefined, minWidth: 0, overflowWrap: 'anywhere' }}>{title}</Typography>
          {help === undefined ? null : <HelpPopover title={typeof title === 'string' ? title : '항목 안내'}>{help}</HelpPopover>}
        </Box>
        {action ? (
          <Box
            data-slot="action"
            sx={{
              flexShrink: 0,
              maxWidth: '100%',
              ml: 'auto',
              '& > .MuiStack-root': { flexWrap: 'wrap', rowGap: 1 },
            }}
          >
            {action}
          </Box>
        ) : null}
      </Stack>
      {showDivider ? <Divider data-slot="header-divider" sx={{ mx: headerInset }} /> : null}
      </Box>
      {bodyPadding !== 'none' ? (
        <Box data-slot="body" sx={{ p: spaciousInset, minWidth: 0 }}>{children}</Box>
      ) : children}
    </Paper>
  );
}
